/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import { ELSPropsFromModalService, ELSWithModalService } from '@els/els-component-modal-react';
import { flowRight } from 'lodash';
import { ELSButton } from '@els/els-component-button-react';
import { useHistory } from 'react-router';
import { ELSDataTable, ELSTooltip } from '../../components/els.components';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import {
  getActivityTypeTitle,
  getCorrectOnFirstAttemptCount,
  getQuizStatusDisplay,
  getRequirementSubtitle,
} from './remediation-student-activity.utilities';
import {
  getContentTypeMainDisplay,
  getContentTypesFromActivityType,
  getHourAndMinuteFromSecond,
  getMetricCounts,
  getQuizAttemptFromBaseState,
  getRecommendationFromAttempt,
  getUniqueAttemptedQuestionCount,
  getUniqueCorrectQuestionCount,
  handleNavigateToContentPerformance,
} from '../../utilities/remediation.utilities';
import {
  ActivityTypeMetricsDto,
  QuestionSubmissionDetailsDto,
  QuizAttemptDetailsDto,
  RemediationActivityTypeDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { ELSButtonSize, ELSButtonType } from '../../models/button.models';
import {
  EmptyValueDisplay,
  NotApplicableDisplay,
  RaasQuizMetricTypeDisplayName
} from './remediation-student-activity.constants';
import { RaasQuizMetricType } from './remediation-student-activity.models';

import { RoutePath } from '../../components/app/app.constants';
import { AppContext } from '../../components/app/AppProvider.component';
import IsRender from '../../components/is-render/IsRender.component';
import {
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import moment from 'moment';

export type RaasQuizProgressSectionProps = ELSPropsFromModalService;

// eslint-disable-next-line sonarjs/cognitive-complexity
const RaasQuizProgressSectionComponent = () => {
  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const history = useHistory();

  if (!baseState?.eolsAssessment?.assessmentGoals && !baseState?.quizAttemptDetails?.questionSubmissionDetails) {
    return null;
  }
  const { assessmentGoals } = baseState.eolsAssessment;

  const noScoreUntilEnoughAttemptsDisplay = (
    <ELSTooltip
      showWhenHover
      content="Score will be available after student has attempted all items"
    >
      <span>{EmptyValueDisplay}</span>
    </ELSTooltip>
  );

  const handleViewPerformance = () => {

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:viewQuizResults`,
        }
      }
    });

    const recommendationAttempt = getQuizAttemptFromBaseState(baseState);
    if (!recommendationAttempt) {
      return;
    }

    const recommendation = getRecommendationFromAttempt(baseState.recommendations, recommendationAttempt);

    if (recommendation) {
        trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.QUIZ_RESULTS_VIEWED,
          props: {
            recommendationId: recommendation.id,
            recommendationAttemptId: recommendationAttempt.id,
            remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
            assessmentId: baseState.eolsAssessment?.id
          }
        }
      });
    }

    handleNavigateToContentPerformance({
      appState,
      appStateRef,
      setAppState,
      assessment: baseState?.eolsAssessment,
      recommendation,
      recommendationAttempt,
      quizSession: null,
      history,
      ref: RoutePath.REMEDIATION_STUDENT_DETAIL
    });
  };

  const showViewQuizResults = (questionAttemptDetails: QuizAttemptDetailsDto) => {
    if (!questionAttemptDetails || !questionAttemptDetails.questionSubmissionDetails) {
      return false;
    }

    const {
      totalItems,
      questionSubmissionDetails
    } = questionAttemptDetails;

    const submissionsWithQuestions = questionSubmissionDetails.filter((detail: QuestionSubmissionDetailsDto) => detail.questions && detail.questions.length > 0);

    const attemptedQuestionTotal = getUniqueAttemptedQuestionCount(submissionsWithQuestions);
    return attemptedQuestionTotal === totalItems;
  };

  const getRaasQuizMetricsForTable = (questionAttemptDetails: QuizAttemptDetailsDto, metricCounts: ActivityTypeMetricsDto) => {
    if (!questionAttemptDetails || !questionAttemptDetails.questionSubmissionDetails) {
      let placeholderToShow = EmptyValueDisplay;

      if (metricCounts.activityTypeAttemptGoal === 0) {
        placeholderToShow = NotApplicableDisplay;
      }

      return [
        {
          description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.QUESTIONS_ATTEMPTED],
          metric: placeholderToShow
        },
        {
          description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.QUESTIONS_CORRECT],
          metric: placeholderToShow
        },
        {
          description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.FIRST_ATTEMPT_SCORE],
          metric: placeholderToShow
        },
        {
          description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.CURRENT_SCORE],
          metric: placeholderToShow
        },
        {
          description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.CURRENT_SCORE],
          metric: placeholderToShow
        }
      ];
    }

    const {
      totalItems,
      questionSubmissionDetails
    } = questionAttemptDetails;

    const submissionsWithQuestions = questionSubmissionDetails.filter((detail: QuestionSubmissionDetailsDto) => detail.questions && detail.questions.length > 0);

    const correctQuestionTotal = getUniqueCorrectQuestionCount(submissionsWithQuestions);
    const attemptedQuestionTotal = getUniqueAttemptedQuestionCount(submissionsWithQuestions);

    const firstAttemptScore = attemptedQuestionTotal === totalItems && totalItems > 0
      ? `${Math.round((getCorrectOnFirstAttemptCount(questionAttemptDetails) / totalItems) * 100)}%`
      : noScoreUntilEnoughAttemptsDisplay;
    const currentScore = attemptedQuestionTotal === totalItems && totalItems > 0
      ? `${Math.round((correctQuestionTotal / totalItems) * 100)}%`
      : noScoreUntilEnoughAttemptsDisplay;

    const totalTimeSpent = getHourAndMinuteFromSecond(moment.duration(metricCounts.timeSpent).asSeconds());

    return [
      {
        description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.QUESTIONS_ATTEMPTED],
        metric: `${attemptedQuestionTotal}/${totalItems}`
      },
      {
        description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.QUESTIONS_CORRECT],
        metric: `${correctQuestionTotal}/${totalItems}`
      },
      {
        description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.FIRST_ATTEMPT_SCORE],
        metric: firstAttemptScore
      },
      {
        description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.CURRENT_SCORE],
        metric: currentScore
      },
      {
        description: RaasQuizMetricTypeDisplayName[RaasQuizMetricType.TOTAL_TIME_SPENT],
        metric: totalTimeSpent
      }
    ];
  };

  const activityType = RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE;
  const assessRecommendation = baseState.recommendations?.find((recommendation) => recommendation.assessmentGoalType === activityType);
  const metricCounts = getMetricCounts(baseState, activityType)
  const contentTypes = getContentTypesFromActivityType(activityType, baseState);
  const contentTypesDisplay = getContentTypeMainDisplay(contentTypes, null);

  const raasQuizMetricsForTable = getRaasQuizMetricsForTable(baseState.quizAttemptDetails, metricCounts);

  return (
    <>
      <div className="u-els-margin-top-2x u-els-margin-bottom-1x">
        <FlexLayout
          modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.GUTTERS]}
        >
          <FlexItem>
            <div>
              <span className="u-els-font-size-body-large u-els-bold u-els-display-block">{getActivityTypeTitle(activityType, contentTypesDisplay)}</span>
              <span className="u-els-color-n7">
                {getRequirementSubtitle(baseState.remediationAssignmentWithSources, assessmentGoals, activityType, metricCounts, contentTypesDisplay)}
              </span>
            </div>
          </FlexItem>
          <FlexItem classes={['u-els-margin-right-1x', 'u-els-margin-left-1x']}>
            <IsRender isRender={!!baseState?.quizAttemptDetails?.questionSubmissionDetails}>
              <span>
                Status: {getQuizStatusDisplay(baseState, assessRecommendation)}
              </span>
            </IsRender>

            {showViewQuizResults(baseState.quizAttemptDetails)
              && (
                <span className="u-els-margin-left-1x1o2">
                  <ELSButton
                    size={ELSButtonSize.SMALL}
                    type={ELSButtonType.SECONDARY}
                    onClick={handleViewPerformance}
                  >
                    View quiz results
                  </ELSButton>
                </span>
              )}
          </FlexItem>
        </FlexLayout>

      </div>
      <ELSDataTable
        data={raasQuizMetricsForTable}
        noWrap
        columns={[
          {
            header: 'Description',
            field: 'description',
          },
          {
            header: 'Metric',
            field: 'metric',
          }
        ]}
      >
      </ELSDataTable>
    </>
  );
};

const enhance = flowRight(ELSWithModalService);

const RaasQuizProgressSection:React.FC<Record<string, never>> = enhance(RaasQuizProgressSectionComponent);

export default RaasQuizProgressSection;
